import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/services/api';
import { getTenantLoginContext } from '@/utils/subdomain';
import { hardNavigate } from '@/desktop/navigation';
import { isDesktopApp } from '@/desktop/electronApi';
import { persistDesktopCloudSession } from '@/desktop/cloudSession';
import { Lock, Unlock, Loader2, AlertTriangle } from 'lucide-react';

interface Props {
  onUnlock: () => void;
}

export default function LockScreenModal({ onUnlock }: Props) {
  const { t } = useTranslation();
  const { user, company, setAuth, clearAuth } = useAuthStore();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.email) {
      setError(t('lockScreen.noSession'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      const ctx = getTenantLoginContext();
      const res = await authApi.login({
        username: user.email,
        password: password,
        ...(ctx.isTenantLogin && ctx.subdomain
          ? { subdomain: ctx.subdomain }
          : { company_code: company?.code || undefined }),
      });
      const data = res.data.message;
      if (isDesktopApp() && data.sid) {
        await persistDesktopCloudSession({
          sid: data.sid,
          userId: data.user?.name || user.name || user.email,
          fullName: data.user?.full_name,
        });
      } else if (data.sid) {
        document.cookie = `sid=${data.sid}; path=/; SameSite=Lax`;
      }
      setAuth(data.user, data.company, data.role, data.can_use_pos);
      onUnlock();
    } catch (err: unknown) {
      let msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      if ((err as { response?: { data?: { _server_messages?: string } } })?.response?.data?._server_messages) {
        try {
          const messages = JSON.parse((err as { response: { data: { _server_messages: string } } }).response.data._server_messages);
          if (messages && messages.length > 0) {
            msg = JSON.parse(messages[0]).message;
          }
        } catch {
          // ignore
        }
      }
      if (msg === 'No App') msg = 'Invalid Company Code or Subdomain.';
      setError(msg || t('lockScreen.invalidPassword'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore
    }
    clearAuth();
    hardNavigate('/login');
  };

  return (
    <div className="fixed inset-0 bg-gray-900/95 flex flex-col items-center justify-center z-[9999] p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 border border-gray-100 flex flex-col items-center text-center">
        <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-4 ring-8 ring-primary-50/50">
          <Lock className="w-8 h-8 text-primary-600 animate-pulse" />
        </div>

        <h2 className="text-xl font-bold text-gray-900">{t('lockScreen.title')}</h2>
        <p className="text-sm text-gray-500 mt-1 mb-6">
          {t('lockScreen.subtitle')}
        </p>

        <div className="w-full bg-gray-50 border border-gray-100 rounded-xl p-4 mb-6 flex items-center gap-3 text-left">
          <div className="w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold text-lg select-none">
            {user?.full_name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">{user?.full_name || t('lockScreen.cashier')}</p>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="w-full space-y-4">
          <div>
            <input
              type="password"
              placeholder={t('lockScreen.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              className="w-full text-center input-field py-2.5 text-base border-gray-300 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              required
              autoFocus
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl py-2 px-3 flex items-center gap-2 justify-center">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary py-2.5 text-base flex items-center justify-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Unlock className="w-5 h-5" />
            )}
            <span>{loading ? t('lockScreen.unlocking') : t('lockScreen.unlock')}</span>
          </button>
        </form>

        <button
          type="button"
          onClick={handleLogout}
          className="mt-6 text-sm text-gray-500 hover:text-gray-700 underline underline-offset-4 cursor-pointer"
        >
          {t('lockScreen.signOut')}
        </button>
      </div>
    </div>
  );
}
